import { useCallback, useState, type FormEvent } from 'react';
import { api } from '../../lib/api';
import { Banner } from '../../components/Banner';
import { Button } from '../../components/Button';
import { Chip } from '../../components/Chip';
import { Input } from '../../components/Input';
import { Modal } from '../../components/Modal';
import { Table, type Column } from '../../components/Table';
import { errorMessage, formatDate, useLoad } from '../members/hooks';
import { CATEGORY_LABEL, type Domain, type ReservedCategory, type ReservedName } from './types';

export function DomainsPage() {
  const loadDomains = useCallback(() => api.get<{ items: Domain[] }>('/v1/domains').then((r) => r.items), []);
  const domains = useLoad(loadDomains);
  const [editing, setEditing] = useState<Domain | null>(null);
  const [notice, setNotice] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  const closeEditing = useCallback(() => setEditing(null), []);

  async function toggle(domain: Domain) {
    setNotice(null);
    try {
      await api.patch(`/v1/domains/${encodeURIComponent(domain.name)}`, { active: !domain.active });
      setNotice({ tone: 'success', text: `${domain.name} quedó ${domain.active ? 'inactivo: sus links siguen resolviendo, pero no se emiten QR nuevos' : 'activo'}.` });
      await domains.reload();
    } catch (err) {
      setNotice({ tone: 'error', text: errorMessage(err) });
    }
  }

  const columns: Column<Domain>[] = [
    { key: 'name', header: 'Dominio', mono: true, render: (d) => d.name },
    { key: 'primary', header: 'Tipo', render: (d) => (d.primary ? <Chip tone="premium">Principal</Chip> : 'Secundario') },
    { key: 'active', header: 'Estado', render: (d) => (d.active ? <Chip tone="success">Activo</Chip> : <Chip>Inactivo</Chip>) },
    { key: 'orgs', header: 'Organizaciones permitidas', render: (d) => (d.primary ? <span className="text-ink-muted">Todas</span> : d.allowedOrgIds.join(', ') || <span className="text-ink-muted">Solo operador</span>) },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (d) => (
        <span className="flex justify-end gap-1">
          {!d.primary && <Button variant="ghost" size="sm" onClick={() => setEditing(d)}>Organizaciones</Button>}
          {!d.primary && <Button variant="ghost" size="sm" onClick={() => void toggle(d)}>{d.active ? 'Desactivar' : 'Activar'}</Button>}
        </span>
      ),
    },
  ];

  return (
    <section className="space-y-6">
      <div>
        <h1 className="font-display text-xl font-bold text-ink">Dominios y nombres reservados</h1>
        <p className="text-sm text-ink-muted">q-r.cl es el dominio principal; cada dominio tiene sus propios códigos.</p>
      </div>
      <div aria-live="polite">{notice && <Banner tone={notice.tone}>{notice.text}</Banner>}</div>
      {domains.error && <Banner tone="error">{domains.error}</Banner>}
      <Table caption="Dominios de links" columns={columns} rows={domains.data ?? []} rowKey={(d) => d.name} loading={domains.loading && !domains.data} />
      <ReservedNames />
      <AllowedOrgsModal
        domain={editing}
        onClose={closeEditing}
        onSaved={async (d) => {
          setEditing(null);
          setNotice({ tone: 'success', text: `Actualizamos las organizaciones permitidas en ${d.name}.` });
          await domains.reload();
        }}
      />
    </section>
  );
}

function AllowedOrgsModal({ domain, onClose, onSaved }: { domain: Domain | null; onClose: () => void; onSaved: (d: Domain) => Promise<void> }) {
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [last, setLast] = useState<Domain | null>(null);
  if (domain && domain !== last) {
    setLast(domain);
    setValue(domain.allowedOrgIds.join(', '));
    setError(null);
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!domain) return;
    setSaving(true);
    setError(null);
    try {
      const ids = value.split(',').map((v) => v.trim()).filter(Boolean);
      const saved = await api.patch<Domain>(`/v1/domains/${encodeURIComponent(domain.name)}`, { allowedOrgIds: ids });
      await onSaved(saved);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal open={domain !== null} onClose={onClose} size="sm" title={`Organizaciones en ${domain?.name ?? ''}`}>
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        <Input label="Identificadores separados por coma" mono className="normal-case" value={value} onChange={(e) => setValue(e.target.value)} error={error ?? undefined} hint="Vacío: solo el operador emite en este dominio." />
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Cancelar</Button>
          <Button type="submit" loading={saving}>Guardar</Button>
        </div>
      </form>
    </Modal>
  );
}

function ReservedNames() {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<ReservedCategory | ''>('');
  const [applied, setApplied] = useState<{ q: string; category: string }>({ q: '', category: '' });
  const loader = useCallback(() => {
    const params = new URLSearchParams({ limit: '500' });
    if (applied.q) params.set('q', applied.q);
    if (applied.category) params.set('category', applied.category);
    return api.get<{ items: ReservedName[]; nextCursor: string | null }>(`/v1/reserved-names?${params}`);
  }, [applied]);
  const { data, error, loading, reload } = useLoad(loader);

  const [newName, setNewName] = useState('');
  const [newCategory, setNewCategory] = useState<ReservedCategory>('marca');
  const [adding, setAdding] = useState(false);
  const [notice, setNotice] = useState<{ tone: 'success' | 'warning' | 'error'; text: string } | null>(null);
  const [removing, setRemoving] = useState<ReservedName | null>(null);
  const closeRemoving = useCallback(() => setRemoving(null), []);

  async function add(e: FormEvent) {
    e.preventDefault();
    setNotice(null);
    if (!newName.trim()) return;
    setAdding(true);
    try {
      const res = await api.post<ReservedName & { inUse: { domain: string; slotId: string }[] }>('/v1/reserved-names', { name: newName.trim(), category: newCategory });
      setNotice(
        res.inUse.length > 0
          ? { tone: 'warning', text: `Reservamos ${res.name}. Ya existe como código en ${res.inUse.map((u) => u.domain).join(', ')}; esos QR siguen funcionando.` }
          : { tone: 'success', text: `Reservamos ${res.name}.` },
      );
      setNewName('');
      await reload();
    } catch (err) {
      setNotice({ tone: 'error', text: errorMessage(err) });
    } finally {
      setAdding(false);
    }
  }

  async function remove() {
    if (!removing) return;
    try {
      await api.del(`/v1/reserved-names/${encodeURIComponent(removing.name)}`);
      setNotice({ tone: 'success', text: `${removing.name} ya no está reservado.` });
      setRemoving(null);
      await reload();
    } catch (err) {
      setNotice({ tone: 'error', text: errorMessage(err) });
      setRemoving(null);
    }
  }

  const columns: Column<ReservedName>[] = [
    { key: 'name', header: 'Nombre', mono: true, render: (r) => r.name },
    { key: 'category', header: 'Categoría', render: (r) => (r.category ? CATEGORY_LABEL[r.category] : '—') },
    { key: 'added', header: 'Agregado', render: (r) => formatDate(r.addedAt) },
    { key: 'actions', header: '', align: 'right', render: (r) => <Button variant="ghost" size="sm" onClick={() => setRemoving(r)}>Quitar</Button> },
  ];

  return (
    <div className="space-y-3">
      <h2 className="font-display text-base font-bold text-ink">Nombres reservados</h2>
      <p className="text-sm text-ink-muted">No se entregan como código en ningún dominio. Las marcas se asignan solo a su dueño verificado.</p>

      <form onSubmit={add} className="flex flex-wrap items-end gap-3 rounded-lg border border-line bg-white p-3" aria-label="Agregar nombre reservado">
        <div className="w-56"><Input label="Nombre" mono value={newName} maxLength={64} onChange={(e) => setNewName(e.target.value)} /></div>
        <label className="flex flex-col gap-1 text-xs font-medium text-ink-muted">
          Categoría
          <select className="rounded-sm border border-line bg-white px-2 py-2 text-sm text-ink" value={newCategory} onChange={(e) => setNewCategory(e.target.value as ReservedCategory)}>
            {Object.entries(CATEGORY_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
        <Button type="submit" loading={adding} disabled={!newName.trim()}>Reservar</Button>
      </form>

      <div aria-live="polite">{notice && <Banner tone={notice.tone}>{notice.text}</Banner>}</div>
      {error && <Banner tone="error">{error}</Banner>}

      <form
        onSubmit={(e) => { e.preventDefault(); setApplied({ q: query.trim(), category }); }}
        className="flex flex-wrap items-end gap-3"
        aria-label="Buscar nombres reservados"
      >
        <div className="w-56"><Input label="Buscar" value={query} onChange={(e) => setQuery(e.target.value)} /></div>
        <label className="flex flex-col gap-1 text-xs font-medium text-ink-muted">
          Filtrar por categoría
          <select className="rounded-sm border border-line bg-white px-2 py-2 text-sm text-ink" value={category} onChange={(e) => setCategory(e.target.value as ReservedCategory | '')}>
            <option value="">Todas</option>
            {Object.entries(CATEGORY_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
        <Button type="submit" variant="secondary">Buscar</Button>
      </form>

      <Table caption="Nombres reservados" columns={columns} rows={data?.items ?? []} rowKey={(r) => r.name} loading={loading && !data} empty="Sin nombres reservados con ese filtro." />
      {data?.nextCursor && <p className="text-xs text-ink-muted">Mostrando los primeros 500. Refina la búsqueda para ver el resto.</p>}

      <Modal
        open={removing !== null}
        onClose={closeRemoving}
        size="sm"
        title={`Quitar ${removing?.name ?? ''}`}
        footer={
          <>
            <Button variant="secondary" onClick={closeRemoving}>Cancelar</Button>
            <Button variant="danger" onClick={() => void remove()}>Quitar</Button>
          </>
        }
      >
        <p className="text-sm">El nombre quedará disponible para asignarse como código.</p>
      </Modal>
    </div>
  );
}
